/**
 * Kundprojekt i Excelexporterna: flikarna Kundprojekt, Kundprojektparter och
 * Kundprojektkontakter, och kundernas säkerhetskolumner. Ren logik – samma
 * data till båda varianterna:
 *   analys  ASCII-nycklar som kolumnnamn, id-kolumnerna först (analysExport.js)
 *   vanlig  svenska rubriker, id-kolumnerna dolda i en grupp (vanligExport.js)
 *
 * Urval: kundprojekt där minst en part är en kund i exportens urval, så att
 * även kundprojekt utan lead kommer med.
 */
import {
  etikett, visaSakerhetsfalt, KP_TYPER, KP_BYGGSKEDEN, KP_ENTREPRENADFORMER, KP_STORLEKSKLASSER,
  KP_DRIFTKRAV, KP_STATUSAR, KP_KALLOR, JA_NEJ_OKANT, KP_IT_MILJOER, KP_PROJEKTKONTOR,
  KP_FILOVERFORING, KP_ROLLER,
} from './kundprojekt';
import { toMillis, lokalDatum } from './pipelineFlode';

const datum = val => { const t = toMillis(val); return t == null ? '' : lokalDatum(t); };

/** Kundprojekt med minst en part bland kunderna i urvalet, sorterade på namn. */
export function urvalKundprojekt(kundprojekt, kundIds) {
  const kunder = new Set(kundIds);
  return (kundprojekt || [])
    .filter(kp => (kp.parter || []).some(p => kunder.has(p.kundId)))
    .sort((a, b) => String(a.namn || '').localeCompare(String(b.namn || ''), 'sv'));
}

/** Kundens säkerhetskolumner (samma nycklar i båda varianterna). */
export function kundSakerhetsfalt(c) {
  return {
    erfarenhet_sakerhetsskydd: etikett(JA_NEJ_OKANT, c.erfarenhetSakerhetsskydd) || '',
    har_isolerad_miljo: etikett(JA_NEJ_OKANT, c.harIsoleradMiljo) || '',
    sakerhets_anteckning: c.sakerhetsAnteckning || '',
  };
}

/**
 * Ett kundprojekts alla fält som en rad. Säkerhetsfälten (utom
 * sakerhetsskydd) lämnas tomma när sakerhetsskydd är nej.
 * @param {object} h  { kundNamn(id), leadTitel(id), kontaktNamn(id), anvandare(uid) }
 */
function kundprojektRad(kp, h) {
  const sak = visaSakerhetsfalt(kp);
  const s = (lista, key) => (sak ? etikett(lista, key) : '');
  const testkorningar = Object.entries(kp.testkorningar || {}).map(([leadId, t]) => ({ leadId, ...t }));
  return {
    kundprojekt_id: kp.id,
    namn: kp.namn || '',
    status: etikett(KP_STATUSAR, kp.status || 'aktivt'),
    typ: etikett(KP_TYPER, kp.typ),
    ort: kp.ort || '',
    byggskede: etikett(KP_BYGGSKEDEN, kp.byggskede),
    skede_start: kp.skedeStart || '',
    nasta_skede: etikett(KP_BYGGSKEDEN, kp.nastaSkede),
    entreprenadform: etikett(KP_ENTREPRENADFORMER, kp.entreprenadform),
    storleksklass: etikett(KP_STORLEKSKLASSER, kp.storleksklass),
    driftkrav: etikett(KP_DRIFTKRAV, kp.driftkrav),
    verktyg_idag: kp.verktygIdag || '',
    antal_granskare: kp.antalGranskare ?? '',
    sakerhetsskydd: etikett(JA_NEJ_OKANT, kp.sakerhetsskydd || 'okant'),
    it_miljo: s(KP_IT_MILJOER, kp.itMiljo),
    projektkontor: s(KP_PROJEKTKONTOR, kp.projektkontor),
    sakerhetsskyddsavtal: s(JA_NEJ_OKANT, kp.sakerhetsskyddsavtal),
    externa_parter_behover_atkomst: s(JA_NEJ_OKANT, kp.externaParterBehoverAtkomst),
    filoverforing: s(KP_FILOVERFORING, kp.filoverforing),
    granskning_idag: sak ? (kp.granskningIdag || '') : '',
    parter: (kp.parter || []).map(p => `${h.kundNamn(p.kundId)}${p.roll ? ` (${etikett(KP_ROLLER, p.roll)})` : ''}`).join('; '),
    testkorningar: testkorningar.map(t => [
      h.leadTitel(t.leadId), [t.start, t.slut].filter(Boolean).join('–'),
      t.ansvarigKontaktId && `ansvarig ${h.kontaktNamn(t.ansvarigKontaktId)}`,
    ].filter(Boolean).join(' ')).join('; '),
    kalla: etikett(KP_KALLOR, kp.kalla),
    registrerad_vid_lead_id: kp.registreradVid?.leadId || '',
    registrerad_vid_aktivitet_id: kp.registreradVid?.aktivitetId || '',
    extern_kalla_system: kp.externKalla?.system || '',
    extern_kalla_id: kp.externKalla?.externtId || '',
    extern_kalla_url: kp.externKalla?.url || '',
    beskrivning: kp.beskrivning || '',
    skapad: datum(kp.skapad),
    skapad_av: h.anvandare(kp.skapadAv),
    uppdaterad: datum(kp.uppdaterad),
    uppdaterad_av: h.anvandare(kp.uppdateradAv),
  };
}

/**
 * Rader för de tre flikarna.
 * @param {object} d  kundprojekt (urvalet), companies, leads (alla, för titlar),
 *   contactsByCompany, users
 */
export function kundprojektRader({ kundprojekt, companies = [], leads = [], contactsByCompany = {}, users = [] }) {
  const kundMap = new Map(companies.map(c => [c.id, c]));
  const leadMap = new Map(leads.map(l => [l.id, l]));
  const kontaktMap = new Map(Object.values(contactsByCompany).flat().map(c => [c.id, c]));
  const h = {
    kundNamn: id => kundMap.get(id)?.name || '',
    leadTitel: id => leadMap.get(id)?.title || id,
    kontaktNamn: id => kontaktMap.get(id)?.name || id,
    anvandare: id => { const u = users.find(x => x.id === id); return u ? (u.displayName || u.name || u.email || id) : (id || ''); },
  };

  const kp = kundprojekt.map(k => kundprojektRad(k, h));
  const parter = kundprojekt.flatMap(k => (k.parter || []).map(p => ({
    kundprojekt_id: k.id,
    kund_id: p.kundId,
    roll: etikett(KP_ROLLER, p.roll),
    lead_ids: (p.leadIds || []).join(', '),
    // För människor (vanlig export); analysexporten tar bara kolumnerna ovan.
    _kundprojekt: k.namn || '',
    _kund: h.kundNamn(p.kundId),
    _leads: (p.leadIds || []).map(h.leadTitel).join(', '),
  })));
  const kontakter = kundprojekt.flatMap(k => (k.parter || []).flatMap(p => (p.kontakter || []).map(c => ({
    kundprojekt_id: k.id,
    kontakt_id: c.kontaktId,
    roll_i_projektet: c.rollIProjektet || '',
    _kundprojekt: k.namn || '',
    _kontakt: h.kontaktNamn(c.kontaktId),
    _kund: h.kundNamn(p.kundId),
  }))));
  return { kp, parter, kontakter };
}

/** Utan fält som börjar med _ (de mänskliga namnen). */
export const utanHjalpfalt = rad => Object.fromEntries(Object.entries(rad).filter(([k]) => !k.startsWith('_')));
